import { useState, useEffect, useCallback } from 'react';
import { AuthenticationResult } from '@azure/msal-browser';
import * as azureAuthService from '../services/azureAuthService';
import { AuthState, AzureAuthError } from '../types/auth';

const initialAuthState: AuthState = {
  isAuthenticated: false,
  isLoading: true,
  user: null,
  error: null,
  accessToken: null
};

export const useAzureAuth = () => {
  const [authState, setAuthState] = useState<AuthState>(initialAuthState);

  // Initialize auth service and check for existing authentication
  useEffect(() => {
    const initializeAuth = async () => {
      try {
        await azureAuthService.initialize();

        // Check if user is already authenticated
        if (azureAuthService.isAuthenticated()) {
          const user = azureAuthService.getUserInfo();
          const accessToken = await azureAuthService.getAccessToken();

          setAuthState({
            isAuthenticated: true,
            isLoading: false,
            user,
            error: null,
            accessToken
          });
        } else {
          setAuthState(prev => ({
            ...prev,
            isLoading: false
          }));
        }
      } catch (error) {
        console.error('Auth initialization failed:', error);
        setAuthState({
          isAuthenticated: false,
          isLoading: false,
          user: null,
          error:
            error instanceof Error
              ? error.message
              : 'Authentication initialization failed',
          accessToken: null
        });
      }
    };

    initializeAuth();
  }, []);

  const login = useCallback(async (): Promise<void> => {
    try {
      setAuthState(prev => ({ ...prev, isLoading: true, error: null }));
      await azureAuthService.loginRedirect();
    } catch (error) {
      console.error('Login failed:', error);
      const authError = error as AzureAuthError;
      setAuthState(prev => ({
        ...prev,
        isLoading: false,
        error: authError.errorMessage || 'Login failed'
      }));
    }
  }, []);

  const logout = useCallback(async (): Promise<void> => {
    try {
      setAuthState(prev => ({ ...prev, isLoading: true, error: null }));
      await azureAuthService.logout();

      setAuthState({
        isAuthenticated: false,
        isLoading: false,
        user: null,
        error: null,
        accessToken: null
      });
    } catch (error) {
      console.error('Logout failed:', error);
      const authError = error as AzureAuthError;
      setAuthState(prev => ({
        ...prev,
        isLoading: false,
        error: authError.errorMessage || 'Logout failed'
      }));
    }
  }, []);

  const handleAuthCallback =
    useCallback(async (): Promise<AuthenticationResult | null> => {
      try {
        setAuthState(prev => ({ ...prev, isLoading: true, error: null }));

        const response = await azureAuthService.handleRedirectCallback();

        if (response && response.account) {
          const user = azureAuthService.getUserInfo();
          const accessToken = response.accessToken;

          setAuthState({
            isAuthenticated: true,
            isLoading: false,
            user,
            error: null,
            accessToken
          });

          return response;
        } else {
          setAuthState(prev => ({
            ...prev,
            isLoading: false
          }));
          return null;
        }
      } catch (error) {
        console.error('Auth callback handling failed:', error);
        const authError = error as AzureAuthError;
        setAuthState({
          isAuthenticated: false,
          isLoading: false,
          user: null,
          error: authError.errorMessage || 'Authentication callback failed',
          accessToken: null
        });
        return null;
      }
    }, []);

  const refreshToken = useCallback(async (): Promise<string | null> => {
    try {
      const accessToken = await azureAuthService.getAccessToken();
      setAuthState(prev => ({
        ...prev,
        accessToken,
        error: null
      }));
      return accessToken;
    } catch (error) {
      console.error('Token refresh failed:', error);
      const authError = error as AzureAuthError;
      setAuthState(prev => ({
        ...prev,
        error: authError.errorMessage || 'Token refresh failed'
      }));
      return null;
    }
  }, []);

  const clearError = useCallback((): void => {
    setAuthState(prev => ({ ...prev, error: null }));
  }, []);

  const clearCache = useCallback((): void => {
    azureAuthService.clearCache();
    setAuthState({
      isAuthenticated: false,
      isLoading: false,
      user: null,
      error: null,
      accessToken: null
    });
  }, []);

  return {
    authState,
    login,
    logout,
    handleAuthCallback,
    refreshToken,
    clearError,
    clearCache
  };
};
